import React, { useState, useEffect, useMemo } from 'react';

import { MailOutlined, UserOutlined } from '@ant-design/icons';
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Space,
  Alert,
  Row,
  Col,
  message,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import MerchantPermissionManager from '@components/shared/merchants/MerchantPermissionManager';
import { useOperatorAuth } from '@contexts/operator/OperatorAuthContext';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import { useIsStandard } from '@contexts/operator/PlanContext';
import { merchantService } from '@services/operator/merchantService';
import { permissionTemplateService } from '@services/operator/permissionTemplateService';
import { filterGrantable } from '@utils/permissionSubset';
import {
  ACTIONS,
  RESOURCES_BY_ROLE,
  RESOURCE_ACTIONS_BY_ROLE,
} from '../../../permissions_manifest.generated';

import TemplateSelect from '../permissions/TemplateSelect';

interface InvitationCreateResult {
  success: boolean;
  data?: { invitation_url?: string; [key: string]: unknown };
  errors?: Array<{ detail?: string; [key: string]: unknown }>;
  message?: string;
  [key: string]: unknown;
}

interface OperatorInvitationServiceLike {
  create: (payload: Record<string, unknown>) => Promise<InvitationCreateResult>;
  [key: string]: unknown;
}

interface WarehouseListResponse {
  success: boolean;
  data?: Array<{ id: string; name: string; [key: string]: unknown }>;
  [key: string]: unknown;
}

interface WarehouseServiceLike {
  fetchList: () => Promise<WarehouseListResponse>;
  [key: string]: unknown;
}

interface WarehouseOption {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface FormValues {
  email?: string;
  username?: string;
  warehouse_id?: string;
  permission_template_id?: string | null;
}

interface CreateOperatorInvitationModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  operatorInvitationService: OperatorInvitationServiceLike;
  warehouseService: WarehouseServiceLike;
}

const CreateOperatorInvitationModal: React.FC<CreateOperatorInvitationModalProps> = ({
  open,
  onClose,
  onSuccess,
  operatorInvitationService,
  warehouseService,
}) => {
  const { t } = useTranslation();
  const { operator: currentOperator } = useOperatorAuth();
  const isStandard = useIsStandard();
  const { merchants: orgMerchants = [] } = useOperatorData();
  // TemplateSelect 仍是 .jsx；TS 對其 JSDoc 推出的 props 型別不正確，cast 後保留 runtime 行為。
  // 放在 component body 內以避免 Vite HMR 下的 top-level TDZ。
  const TemplateSelectComp = TemplateSelect as React.ComponentType<any>;
  const [form] = Form.useForm<FormValues>();
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [selectedMerchantIds, setSelectedMerchantIds] = useState<string[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [strippedCount, setStrippedCount] = useState(0);
  // 紀錄選擇的範本因 granter 權限不足被「整批剝光」時是否走 fallback（替換成 granter 全集）。
  // 跟 strippedCount > 0 是不同情境：strippedCount 表示「砍掉 N 項仍留下 kept」，
  // replacedWithGranter 表示「kept=0，已用 granter.permissions 替換以免送出按鈕死掉」
  const [replacedWithGranter, setReplacedWithGranter] = useState(false);
  // 監聽 form 中 warehouse_id 變動，下傳給 MerchantPermissionManager 並 reset 已選 merchant
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string | undefined>(undefined);

  // 把 selectedMerchantIds.map(...) 結果穩定化，避免每次 parent re-render（例如選權限範本
  // 觸發 setSelectedPermissions / setStrippedCount）都建立新的 operatorMerchants 陣列 reference，
  // 進而觸發 MerchantPermissionManager L132 effect 不必要的 re-run。
  const operatorMerchantsForChild = useMemo(
    () =>
      selectedMerchantIds.map((id) => {
        const m = orgMerchants.find((mm) => mm.id === id);
        return { id, name: m?.name || '' };
      }),
    [selectedMerchantIds, orgMerchants]
  );

  useEffect(() => {
    if (open) {
      fetchWarehouses();
    }
  }, [open]);

  // Standard 方案 + 唯一 merchant → 預勾
  useEffect(() => {
    if (!open) return;
    if (isStandard && orgMerchants.length === 1) {
      setSelectedMerchantIds([orgMerchants[0].id]);
    }
  }, [open, isStandard, orgMerchants]);

  const fetchWarehouses = async () => {
    try {
      setLoadingWarehouses(true);
      const data = await warehouseService.fetchList();

      if (data.success && data.data) {
        setWarehouses(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch warehouses:', err);
    } finally {
      setLoadingWarehouses(false);
    }
  };

  const handleClose = () => {
    onClose();
    form.resetFields();
    setSelectedPermissions([]);
    setSelectedMerchantIds([]);
    setStrippedCount(0);
    setReplacedWithGranter(false);
    setSelectedWarehouseId(undefined);
  };

  const handleCreate = async (values: FormValues) => {
    try {
      const { permission_template_id, ...rest } = values;
      const result = await operatorInvitationService.create({
        ...rest,
        permissions: selectedPermissions,
        merchant_ids: selectedMerchantIds,
      });

      if (result.success) {
        message.success(t('shared.operator_invitations.messages.create_success'));
        handleClose();
        onSuccess?.();

        const invitationUrl = result.data?.invitation_url;
        if (invitationUrl) {
          Modal.info({
            title: t('shared.operator_invitations.link_title'),
            content: (
              <div>
                <p>{t('shared.operator_invitations.link_description')}</p>
                <Input.TextArea
                  value={invitationUrl}
                  autoSize
                  readOnly
                  onClick={(e) => (e.target as HTMLTextAreaElement).select()}
                  style={{ marginTop: 8 }}
                />
              </div>
            ),
            okText: t('common.button.close'),
          });
        }
      } else {
        const firstError = result.errors?.[0] || {};
        const { detail: errorCode, ...interpolations } = firstError;
        const fallback = t('shared.operator_invitations.error.create_failed');
        const errorMessage = errorCode
          ? t(`shared.operator_invitations.error.${errorCode}`, { ...interpolations, defaultValue: fallback })
          : fallback;
        message.error(errorMessage as string);
      }
    } catch (error) {
      console.error('Failed to create invitation:', error);
      message.error(t('shared.operator_invitations.error.create_failed'));
    }
  };

  return (
    <Modal
      title={t('shared.operator_invitations.modal.create_title')}
      open={open}
      onCancel={handleClose}
      footer={null}
      width={960}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleCreate}
        onValuesChange={(changed) => {
          if ('warehouse_id' in changed) {
            const next = changed.warehouse_id as string | undefined;
            setSelectedWarehouseId(next);
            // 切換 warehouse 後，原本選的 merchant 多半不屬於新 warehouse，直接清掉
            // 比讓使用者送出後被 BE 擋更乾淨。
            setSelectedMerchantIds([]);
          }
        }}
      >
        <Row gutter={24}>
          <Col xs={24} md={12}>
        <Form.Item
          name="email"
          label={t('common.table.headers.email')}
          rules={[
            { required: true, message: t('common.validation.email_required') },
            { type: 'email', message: t('common.validation.email_invalid') }
          ]}
        >
          <Input placeholder="user@example.com" prefix={<MailOutlined />} />
        </Form.Item>

        <Form.Item
          name="username"
          label={t('models.operator.username')}
          rules={[
            { required: true, message: t('common.validation.username_required') }
          ]}
        >
          <Input placeholder={t('common.placeholder.please_enter')} prefix={<UserOutlined />} />
        </Form.Item>

        <Form.Item
          name="warehouse_id"
          label={t('models.warehouse.name')}
          rules={[
            { required: true, message: t('common.validation.warehouse_required') }
          ]}
        >
          <Select
            placeholder={t('common.placeholder.please_select')}
            loading={loadingWarehouses}
            options={warehouses.map(w => ({ label: w.name, value: w.id }))}
          />
        </Form.Item>

        <Form.Item
          name="permission_template_id"
          label={t('shared.operator_invitations.fields.permission_template')}
        >
          <TemplateSelectComp
            templateService={permissionTemplateService}
            role="operator"
            onChange={(templateId: string | null, permissions: string[]) => {
              form.setFieldsValue({ permission_template_id: templateId });
              if (!currentOperator?.permissions) {
                setSelectedPermissions(permissions || []);
                setStrippedCount(0);
                setReplacedWithGranter(false);
                return;
              }
              const { kept, dropped } = filterGrantable(
                permissions || [],
                currentOperator.permissions,
                {
                  resources: RESOURCES_BY_ROLE.operator || [],
                  resourceActions: RESOURCE_ACTIONS_BY_ROLE.operator || {},
                  allActions: ACTIONS,
                }
              );
              // 如果範本權限完全超出 granter 可授予範圍（kept 全空），
              // 不要讓使用者陷入「按鈕灰掉、無路可走」的死巷 — 自動替換成 granter
              // 自己擁有的權限作為最大可授予子集，並顯示專屬提示。
              if (kept.length === 0 && dropped.length > 0) {
                setSelectedPermissions(currentOperator.permissions);
                setStrippedCount(0);
                setReplacedWithGranter(true);
              } else {
                setSelectedPermissions(kept);
                setStrippedCount(dropped.length);
                setReplacedWithGranter(false);
              }
            }}
          />
        </Form.Item>
        {strippedCount > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 8, marginBottom: 24 }}
            message={t('common.permissions_editor.template_stripped_count', { count: strippedCount })}
          />
        )}
        {replacedWithGranter && (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 8, marginBottom: 24 }}
            message={t('common.permissions_editor.template_replaced_with_grantable')}
          />
        )}
        {selectedPermissions.length === 0 && (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 8, marginBottom: 24 }}
            message={t('shared.operator_invitations.permissions_required_hint')}
          />
        )}
          </Col>

          <Col xs={24} md={12}>
        {orgMerchants.length === 0 ? (
          <Alert
            type="warning"
            showIcon
            message={t('shared.operator_invitations.hints.no_merchant_setup_yet')}
            description={
              <Link to="/my_merchant">
                {t('operator.menu.merchant_settings.my_merchant')}
              </Link>
            }
            style={{ marginBottom: 16 }}
          />
        ) : (
          <Form.Item
            label={t('shared.operator_invitations.fields.merchants')}
            required
          >
            <MerchantPermissionManager
              mode="invite"
              operatorService={{}}
              merchantService={merchantService}
              operatorMerchants={operatorMerchantsForChild}
              operatorId={null}
              canEdit
              onChange={setSelectedMerchantIds}
              warehouseId={selectedWarehouseId}
            />
            {selectedMerchantIds.length === 0 && (
              <Alert
                type="info"
                showIcon
                style={{ marginTop: 8 }}
                message={t('shared.operator_invitations.hints.select_at_least_one_merchant')}
              />
            )}
          </Form.Item>
        )}
          </Col>
        </Row>

        <Form.Item style={{ marginTop: 16 }}>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              disabled={
                selectedPermissions.length === 0 ||
                selectedMerchantIds.length === 0 ||
                orgMerchants.length === 0
              }
            >
              {t('common.button.send')}
            </Button>
            <Button onClick={handleClose}>
              {t('common.button.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateOperatorInvitationModal;
